import { useState, useEffect, useRef } from 'react';

export interface StreamHealth {
  bitrate: number;
  framerate: number;
  droppedFrames: number;
  connectionQuality: number;
  uptime: number;
  isHealthy: boolean;
  status: 'excellent' | 'good' | 'fair' | 'poor';
}

interface UseStreamHealthOptions {
  streamId?: string;
  isLive?: boolean;
  onHealthChange?: (health: StreamHealth) => void;
  warningThreshold?: number;
  criticalThreshold?: number;
}

export const useStreamHealth = ({
  streamId,
  isLive,
  onHealthChange,
  warningThreshold = 85,
  criticalThreshold = 70,
}: UseStreamHealthOptions) => {
  const [health, setHealth] = useState<StreamHealth>({
    bitrate: 0,
    framerate: 0,
    droppedFrames: 0,
    connectionQuality: 100,
    uptime: 0,
    isHealthy: true,
    status: 'excellent',
  });

  const intervalRef = useRef<NodeJS.Timeout>();
  const startTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!isLive || !streamId) {
      // Reset when stream ends
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      setHealth({
        bitrate: 0,
        framerate: 0,
        droppedFrames: 0,
        connectionQuality: 100,
        uptime: 0,
        isHealthy: true,
        status: 'excellent',
      });
      return;
    }

    startTimeRef.current = Date.now();

    // Simulate health monitoring - In production, this would connect to actual stream metrics
    intervalRef.current = setInterval(() => {
      const newHealth: StreamHealth = {
        bitrate: 6000 + Math.random() * 1000, // Simulate 6-7 Mbps
        framerate: 58 + Math.random() * 4, // Simulate 58-62 fps
        droppedFrames: Math.random() * 2, // Simulate 0-2% dropped frames
        connectionQuality: 95 + Math.random() * 5, // Simulate 95-100% quality
        uptime: Math.floor((Date.now() - startTimeRef.current) / 1000),
        isHealthy: true,
        status: 'excellent',
      };

      // Calculate health status
      if (newHealth.connectionQuality > 95 && newHealth.droppedFrames < 1) {
        newHealth.status = 'excellent';
        newHealth.isHealthy = true;
      } else if (newHealth.connectionQuality > warningThreshold && newHealth.droppedFrames < 3) {
        newHealth.status = 'good';
        newHealth.isHealthy = true;
      } else if (newHealth.connectionQuality > criticalThreshold) {
        newHealth.status = 'fair';
        newHealth.isHealthy = true;
      } else {
        newHealth.status = 'poor';
        newHealth.isHealthy = false;
      }

      setHealth(newHealth);
      if (onHealthChange) {
        onHealthChange(newHealth);
      }
    }, 2000); // Update every 2 seconds

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isLive, streamId, warningThreshold, criticalThreshold]);

  return health;
};
